import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Mutation that refetches the queries under `queryKey` once it succeeds, and shows the
 * `errorMessageKey` alert when it fails. Stays pending until the refetch finishes.
 * @param queryKey - Key (prefix) of the queries the mutation changes.
 * @param mutationFn - The API call.
 * @param errorMessageKey - Translation key of the error alert.
 * @param optimisticUpdate - Optionally patches every cached list under `queryKey` right away.
 *   The refetch on settle replaces the patch, including after a failure.
 * @param alsoInvalidate - Other keys (prefixes) of queries that embed the changed data.
 */
export const useInvalidatingMutation = <
  TVariables = void,
  TData = unknown,
  TItem = never,
>(
  queryKey: QueryKey,
  mutationFn: (variables: TVariables) => Promise<TData>,
  errorMessageKey: string,
  optimisticUpdate?: (list: TItem[], variables: TVariables) => TItem[],
  alsoInvalidate: QueryKey[] = [],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    // Lets `useIsMutating({ mutationKey: queryKey })` show a spinner while anything changes these queries
    mutationKey: queryKey,
    mutationFn,
    onMutate: async (variables) => {
      if (!optimisticUpdate) return;
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueriesData<TItem[]>(
        { queryKey },
        (list) => list && optimisticUpdate(list, variables),
      );
    },
    // After a failure, refetch only to undo the optimistic patch: nothing else changed
    onSettled: (_data, error) =>
      error && !optimisticUpdate
        ? undefined
        : Promise.all(
            [queryKey, ...alsoInvalidate].map((key) =>
              queryClient.invalidateQueries({ queryKey: key }),
            ),
          ),
    meta: { errorMessageKey },
  });
};
